import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Calendar, MapPin, ExternalLink, Award, Loader2 } from "lucide-react";
import { formatDateLong, formatDateRange } from "../lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FestivalDetail() {
  const { id } = useParams();
  const [festival, setFestival] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFestival();
  }, [id]);

  const fetchFestival = async () => {
    try {
      const response = await axios.get(`${API}/festivals/${id}`);
      setFestival(response.data);
    } catch (error) {
      console.error("Error fetching festival:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  if (!festival) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-400 mb-4">Festival not found</p>
        <Link to="/festivals" className="text-purple-light hover:text-white">
          Back to Festivals
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/festivals" 
            data-testid="back-to-festivals"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Festivals
          </Link>

          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="flex items-center gap-2 text-purple-light">
              <Calendar className="w-5 h-5" />
              {formatDateRange(festival.start_date, festival.end_date, formatDateLong)}
            </span>
            <span className="flex items-center gap-2 text-slate-400">
              <MapPin className="w-5 h-5" />
              {festival.location}
            </span>
          </div>

          <h1 className="font-syne text-4xl md:text-5xl font-bold text-white mb-6">
            {festival.name}
          </h1>

          <div className="text-lg text-slate-300 leading-relaxed mb-8 whitespace-pre-line">
            {festival.description}
          </div>

          {festival.submission_deadline && (
            <div className="p-6 bg-purple/10 border border-purple/30 mb-8">
              <h3 className="font-syne font-bold text-white mb-2">Submission Deadline</h3>
              <p className="text-purple-light text-lg">{formatDateLong(festival.submission_deadline)}</p>
            </div>
          )}

          {festival.website_url && (
            <a
              href={festival.website_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="festival-website"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
            >
              Visit Website
              <ExternalLink className="w-4 h-4" />
            </a>
          )}

          {festival.coverage && (
            <div className="mt-12">
              <h2 className="font-syne text-2xl font-bold text-white mb-4">Coverage</h2>
              <div className="text-slate-300 whitespace-pre-wrap">
                {festival.coverage}
              </div>
            </div>
          )}

          {festival.winners && festival.winners.length > 0 && (
            <div className="mt-12">
              <h2 className="font-syne text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-purple-light" />
                Winners
              </h2>
              <div className="space-y-3">
                {festival.winners.map((winner, index) => (
                  <div key={index} className="p-4 bg-void-surface border border-white/5">
                    <p className="text-white">{winner}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
